import { keyframes, Theme } from '@mui/material/styles';

// Slow attention pulse for the Search button while filter changes are
// pending — same easing/duration convention as AutoRefresh's active dot
// (src/components/molecules/Pagination/AutoRefresh.tsx).
export const pendingSearchPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.55; }
  100% { opacity: 1; }
`;

/**
 * Search-button colour/animation for the "filters changed but not yet
 * applied" state, shared by FilterBar and the bespoke bars still to be
 * converged (StructureFilterBar, PartTypeFilterBar) so the affordance stays
 * visually identical across pages and the branching logic isn't duplicated
 * inside each component. See BACKLOG.md for the convergence item — until it
 * lands, a new affordance here has to be wired into each bar by hand.
 */
// The resting button is `accentDark`, not `accent`: white on `accent`
// (#0d9488) is 3.75:1, under WCAG AA for text, where `accentDark` (#0f766e)
// is 5.47:1. The palette already documents `accentDark` as the shade to use
// whenever the teal carries a label, and this button carries one.
//
// The dirty state keeps `warning.dark` but flips its label dark rather than
// white. White on `warning.dark` (#e65100) is 3.79:1, under AA — the gap this
// file used to document as unreachable. It was only unreachable while the label
// stayed white: black on the same amber is 5.54:1, so the state now clears AA
// without inventing an off-palette colour.
export const getSearchButtonDirtySx = (theme: Theme, isDirty: boolean) => ({
  bgcolor: isDirty ? theme.palette.warning.dark : theme.palette.v4.accentDark,
  color: isDirty ? theme.palette.common.black : theme.palette.common.white,
  animation: isDirty ? `${pendingSearchPulse} 2s ease-in-out infinite` : 'none',
  '&:hover': {
    bgcolor: isDirty ? theme.palette.warning.dark : theme.palette.v4.accentDark,
    color: isDirty ? theme.palette.common.black : theme.palette.common.white,
    opacity: 0.9,
  },
});

/**
 * Screen-reader-only text describing the pending state, linked to the Search
 * button via `aria-describedby`. The button's own `aria-label` deliberately
 * stays constant ("Search services", etc.) rather than growing a suffix —
 * changing the accessible *name* based on state would break every existing
 * "find the Search button and click it" flow (real usage and tests alike)
 * that looks it up by that fixed label regardless of dirty state. A
 * description supplements the name without replacing it.
 */
export const SEARCH_BUTTON_PENDING_HINT = 'Filters changed, click to apply';
